import electronicFormService from "@/views/electronicForm/electronicFormService";
import { apiService, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { ToastOptionsSuccess, ToastOptionsError } from "@modernary/mui/components/toasts";
import { apiHosts } from "@/api/constants/apiHosts";

import type {
  ElectronicForm,
  ElectronicFormNew,
  ElectronicFormQuery,
  ElectronicFormBE,
  ElectronicFormSubmissionData,
  ElectronicFormVerifiedData,
  ESignRequest,
} from "./electronicForm";
import type { AxiosResponse } from "axios";
import { LE } from "@/constants/enums";

const URL = "electronic-forms";
const ENTITY_NAME = "Electronic Form";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};
const api = apiService<ElectronicFormBE>(apiHosts.AQMS, axiosParams);

const getDownloadLinkForElectronicForm = (id: string | number | undefined) => {
  if (!id) return "";
  return import.meta.env.VITE_AQMS_API_BASE_URL + URL + "/" + id + "/download";
};

const getElectronicForms = (params: ElectronicFormQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successToastCb,
    errorToastCb,
  });
};

const getElectronicFormById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.get(URL + "/" + id, {
    abort: config?.abortMethod,
    successToastCb,
    errorToastCb,
  });
};

type ElectronicFormNewValues = {
  entityId?: string | number;
  entityType: string;
  statusMurn: LE.ElectronicFormStatus;
  groupMurn: LE.ElectronicFormGroup;
  typeMurn: string;
};
const getNewElectronicForm = async <
  T extends ElectronicFormSubmissionData = ElectronicFormSubmissionData,
  V extends ElectronicFormVerifiedData = ElectronicFormVerifiedData,
>(
  values: ElectronicFormNewValues,
) => {
  const defaultApp = {
    data: {
      ...electronicFormService.getInitialData(),
      ...values,
    },
  } as AxiosResponse<ElectronicFormNew<T, V>, any>;

  return defaultApp;
};

const createElectronicForm = (electronicForm: ElectronicFormBE, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.post(URL, electronicForm, {
    successToastCb,
    errorToastCb,
  });
};

const updateElectronicForm = (electronicForm: ElectronicFormBE, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.put(URL + "/" + electronicForm.id, electronicForm, {
    successToastCb,
    errorToastCb,
  });
};

/**
 * @param {ElectronicForm} electronicForm This cannot be a 'ref' to an electronicForm
 */
const saveElectronicForm = (electronicForm: ElectronicFormBE, config = {}) => {
  if (electronicForm.id) {
    return updateElectronicForm(electronicForm, config);
  } else {
    return createElectronicForm(electronicForm, config);
  }
};

const deleteElectronicFormById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

const eSignElectronicForm = (eSignRequest: ESignRequest, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.title = "Error Submitting Electronic Form";
    options.hideStatusCodes = [403];
    config?.errorToastCb?.(options);
  };

  return api.post(URL + "/" + eSignRequest.id + "/esign", eSignRequest, {
    successToastCb,
    errorToastCb,
  });
};

export {
  getDownloadLinkForElectronicForm,
  deleteElectronicFormById,
  getElectronicForms,
  getElectronicFormById,
  getNewElectronicForm,
  createElectronicForm,
  updateElectronicForm,
  saveElectronicForm,
  eSignElectronicForm,
};
